<template>
  <div class="app-main">
    <router-view v-slot="{ Component, route }">
      <component :key="route.fullPath" :is="Component"></component>
    </router-view>
  </div>
  <DugWarning
    v-model:isShow="drugDialog"
    :data="drugDialogList"
    :z-index="99"
  />
  <PackageWarning v-model:show="packageDialog" :data="packageDialogList" />
</template>

<script lang="ts" setup>
import { isEmpty } from '@/libs/utils/data-checker'
import DugWarning from './alertDialog/drugWarning.vue'
import PackageWarning from './alertDialog/packageWarning.vue'
import { drugDeviceCatalogkEsPage } from '@/api/inventory/yaoxie'
import { getDirectories } from '@/api/login'
import { STORAGE_KEYS, useCache } from '@/libs/utils/constants'
import { useUserStore } from '@/store/modules/user'
import { sideBarStore } from '@/store/modules/sideBar'
import { custFormatDate } from '@/libs/utils/data-jsFn'
import { useWebSocketStore } from '@/store/modules/websocket'
import { useVoicePromptStore } from '@/store/modules/voicePrompt'
import { useDebounceFn } from '@vueuse/core'
import taobaoAudio from '@/assets/audio/taobao.mp3'
import chargeTipAudio from '@/assets/audio/shoufeitip.mp3'
import pharmacyTipAudio from '@/assets/audio/fayaotip.mp3'

const { wsCache } = useCache('localStorage')
const drugDialog = ref(false)
const drugDialogList = ref([])
const packageDialog = ref(false)
const packageDialogList = ref([])
const userStore = useUserStore()
const barStore = sideBarStore()
const voicePromptStore = useVoicePromptStore()
onBeforeMount(() => {
  initPackageDetail()
  initDeptList()
  voicePromptStore.loadSettings()
})
const packageObj = {
  1: '辅助诊疗',
  2: '提成报表',
  3: '会员',
  4: '卡项',
  5: '医生移动端',
  6: '患者端',
  7: '公众号代运营',
  7: '开通账号',
  7: '体验辅助诊疗',
}
const distanceDayFn = time => {
  // 转换为日期对象
  const expireDate = new Date(time)
  const today = new Date()
  // 计算相差天数
  const timeDiff = expireDate.getTime() - today.getTime()
  const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24))
  return daysDiff
}
const initPackageDetail = async () => {
  packageDialogList.value = []
  const daysDiff = distanceDayFn(userStore.getUserInfo.expireTime)
  // 判断是否小于30天
  const isLessThan30Days = daysDiff < 30 && daysDiff >= 0
  if (isLessThan30Days) {
    packageDialogList.value.push({
      name: `门诊系统（${userStore.getUserInfo.packageName}）`,
      reason: custFormatDate(userStore.getUserInfo.expireTime),
    })
  }

  const res = await getDirectories()
  if (res.code == 0) {
    if (!isEmpty(res.data)) {
      res.data.forEach(item => {
        let time = new Date(item.expirationTime)?.getTime()
        if (distanceDayFn(time) < 30 && distanceDayFn(time) >= 0) {
          packageDialogList.value.push({
            name: packageObj[item.type],
            reason: item.expirationTime,
          })
        }
      })
    }
  }
  // 不为空并且不在三天内再次提示
  if (!isEmpty(packageDialogList.value) && shouldShowPackageDialog()) {
    packageDialog.value = true
  }
}
const initDeptList = async () => {
  let arr = []
  // 库存
  const res = await drugDeviceCatalogkEsPage({
    status: 1,
    pageSize: -1,
    stockWarnStatus: true,
  })
  // 效期预警
  const result = await drugDeviceCatalogkEsPage({
    status: 1,
    pageSize: -1,
    validityWarnStatus: true,
  })
  if (result.code == 0) {
    arr = result.data.list.map(e => {
      return {
        ...e,
        reason: '效期不足',
      }
    })
  }
  if (res.code == 0 && res.data.list) {
    res.data.list.forEach(ele => {
      if (arr.some(e => e.id == ele.id)) {
        arr.forEach(e => {
          if (e.id == ele.id) {
            e.reason += '，库存不足'
          }
        })
      } else {
        arr.push({
          ...ele,
          reason: '库存不足',
        })
      }
    })
  }
  if (!isEmpty(arr) && shouldShowDialog()) {
    drugDialogList.value = arr
    drugDialog.value = true
  }
}
// 检查今日是否已关闭
const shouldShowDialog = () => {
  const today = new Date().toDateString()
  const lastCloseDate = wsCache.get('drugDialogClosedDate')
  return lastCloseDate !== today
}
// 检查3日内是否关闭
const shouldShowPackageDialog = () => {
  const lastCloseDate = wsCache.get('packageDialogClosedDate')
  if (!lastCloseDate) return true
  const closedDate = new Date(lastCloseDate)
  const today = new Date()
  const timeDiff = today.getTime() - closedDate.getTime()
  const daysDiff = timeDiff / (1000 * 3600 * 24)

  return daysDiff >= 3
}

const wsStore = useWebSocketStore()
const messageTipConfigMap: Record<
  number,
  {
    audioSrc: string
    isEnabled: () => boolean
    shouldPlay?: (message: Record<string, unknown>) => boolean
  }
> = {
  2000: {
    audioSrc: chargeTipAudio,
    isEnabled: () => voicePromptStore.voicePromptEnabled,
    shouldPlay: message => message.broadcast === true,
  },
  3000: {
    audioSrc: pharmacyTipAudio,
    isEnabled: () => voicePromptStore.proxyDispensingVoicePromptEnabled,
    shouldPlay: message => message.broadcast === true,
  },
}
let audioPlaybackQueue = Promise.resolve()

const normalizeMessage = (message: Record<string, unknown>) => {
  const { content } = message
  if (typeof content === 'object' && content !== null) {
    return { ...message, ...(content as Record<string, unknown>) }
  }
  if (typeof content !== 'string') return message

  try {
    const parsedContent: unknown = JSON.parse(content)
    if (typeof parsedContent === 'object' && parsedContent !== null) {
      return { ...message, ...(parsedContent as Record<string, unknown>) }
    }
  } catch (error) {
    console.warn('消息 content 解析失败:', error)
  }

  return message
}

const playAudio = (audioSrc: string) => {
  return new Promise<void>((resolve, reject) => {
    const audio = new Audio(audioSrc)
    audio.addEventListener('ended', () => resolve(), { once: true })
    audio.addEventListener('error', () => reject(), { once: true })
    audio.play().catch(reject)
  })
}

const enqueueMessageTip = (message: Record<string, unknown>) => {
  const tipConfig = messageTipConfigMap[Number(message.type)]
  if (!tipConfig) return
  if (!tipConfig.isEnabled()) return
  const normalizedMessage = normalizeMessage(message)
  if (tipConfig.shouldPlay && !tipConfig.shouldPlay(normalizedMessage)) return
  audioPlaybackQueue = audioPlaybackQueue
    .then(() => playAudio(taobaoAudio))
    .then(() => playAudio(tipConfig.audioSrc))
    .catch(error => {
      console.error('消息提示音播放失败:', error)
    })
}

watch(
  () => wsStore.data,
  newData => {
    if (newData == 'pong') return
    console.log('刷新角标收到消息', wsStore.data)
    let obj = JSON.parse(wsStore.data) ?? {}

    enqueueMessageTip(obj)
    // obj.type == 1000
    // obj.type == 1001
    // obj.type == 2000
    // obj.type == 3000
    // obj.type == 3001
    // obj.type == 3002
    // obj.type == 3003
    // obj.type == 4000
    // obj.type == 4001
    // obj.type == 4002
    // obj.type == 5000
    // obj.type == 5001
    // obj.type == 5002
    if (obj.type != 6000 && obj.type != 7000 && obj.type != 9999) {
      debouncedFocus()
    }
  }
)
// 创建防抖函数
const debouncedFocus = useDebounceFn(() => {
  barStore.setBarCounts()
}, 1000)
</script>

<style lang="scss" scoped>
.app-main {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: calc(100% - 50px);
  padding-bottom: 0;
}
</style>

<style lang="scss"></style>
